/**
 * `/inbox` — your own notifications.
 *
 * Behind no permission at all, which is why it sits outside the
 * `RequirePermission` groups in `App.tsx`. `notification.read` is the one that
 * looks like it belongs here and does not: that guards the *administration* of
 * channels and routing rules.
 *
 * The rows are the same list the bell shows, with room to breathe and the
 * actions that do not fit in a popover.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import ClearIcon from '@mui/icons-material/Clear';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { UserNotificationDto } from '@forsys/shared';
import { AsyncContent } from '../../components/AsyncContent';
import { EntityTable } from '../../components/EntityTable';
import { ErrorState } from '../../components/ErrorState';
import { PageHeader } from '../../components/PageHeader';
import { Timestamp } from '../../components/Timestamp';
import { UnreadDot } from '../../components/UnreadDot';
import { UnreadFilter } from '../../components/UnreadFilter';
import { useAsync } from '../../hooks/useAsync';
import { useListQuery } from '../../hooks/useListQuery';
import { useSubmit } from '../../hooks/useSubmit';
import { dismiss, listInbox, markAllRead, markRead, markUnread } from '../../api/inbox';

export default function InboxPage() {
  const { t } = useTranslation(['inbox', 'common']);
  const list = useListQuery({ defaultSort: 'createdAt', defaultOrder: 'desc' });
  const [unreadOnly, setUnreadOnly] = useState(false);

  const inbox = useAsync(
    () => listInbox({ ...list.query, ...(unreadOnly ? { unread: true } : {}) }),
    [list.key, unreadOnly],
  );

  const toggleRead = useSubmit(async (notification: UserNotificationDto) => {
    const result =
      notification.readAt === null
        ? await markRead(notification.id)
        : await markUnread(notification.id);
    inbox.reload();
    return result;
  });

  const clear = useSubmit(async (notification: UserNotificationDto) => {
    const result = await dismiss(notification.id);
    inbox.reload();
    return result;
  });

  const readAll = useSubmit(async () => {
    const result = await markAllRead();
    inbox.reload();
    return result;
  });

  const unread = inbox.data?.unreadCount ?? 0;

  return (
    <>
      <PageHeader
        title={t('inbox:title')}
        description={t('inbox:description')}
        actions={
          unread > 0 && (
            <Button
              variant="outlined"
              startIcon={<DoneAllIcon />}
              disabled={readAll.pending}
              onClick={() => void readAll.submit()}
            >
              {t('inbox:markAllRead')}
            </Button>
          )
        }
      />

      {/*
        Said once, here, rather than on every row: notification text is written
        by the server, which is deliberately not localised. Without this line a
        Czech reader sees English sentences in an otherwise Czech interface and
        has no way to know it is intentional rather than broken.
      */}
      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        {t('inbox:untranslated')}
      </Alert>

      {/* One slot for three actions: they are mutually exclusive in practice,
          and three stacked banners for one failure is worse than one. */}
      {(() => {
        const failure = toggleRead.error ?? clear.error ?? readAll.error;
        return failure ? <ErrorState error={failure} /> : null;
      })()}

      <UnreadFilter
        unreadOnly={unreadOnly}
        onChange={setUnreadOnly}
        unreadCount={unread}
        countLabel={t('inbox:unreadCount', { count: unread })}
      />

      <AsyncContent state={inbox}>
        {({ notifications, page }) => (
          <EntityTable
            rows={notifications}
            rowKey={(notification) => notification.id}
            list={list}
            page={page}
            empty={{
              icon: <NotificationsNoneIcon fontSize="inherit" />,
              title: unreadOnly ? t('inbox:noneUnread') : t('inbox:none'),
              description: unreadOnly
                ? t('inbox:noneUnreadDescription')
                : t('inbox:noneDescription'),
            }}
            columns={[
              {
                field: 'subject',
                label: t('inbox:column.subject'),
                render: (notification) => (
                  <Stack spacing={0.5} sx={{ py: 0.5 }}>
                    <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                      <UnreadDot unread={notification.readAt === null} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {notification.subject}
                      </Typography>
                      {notification.severity !== 'info' && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color={notification.severity === 'critical' ? 'error' : 'warning'}
                          label={notification.severity}
                        />
                      )}
                    </Stack>
                    {notification.body !== '' && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ whiteSpace: 'pre-line', pl: 2 }}
                      >
                        {notification.body}
                      </Typography>
                    )}
                  </Stack>
                ),
              },
              {
                field: 'createdAt',
                label: t('inbox:column.received'),
                sortable: true,
                width: 160,
                render: (notification) => <Timestamp value={notification.createdAt} />,
              },
            ]}
            actions={(notification) => [
              {
                key: 'read',
                label: notification.readAt === null ? t('inbox:markRead') : t('inbox:markUnread'),
                icon:
                  notification.readAt === null ? (
                    <DraftsOutlinedIcon fontSize="small" />
                  ) : (
                    <MarkEmailUnreadOutlinedIcon fontSize="small" />
                  ),
                onClick: () => void toggleRead.submit(notification),
              },
              {
                key: 'dismiss',
                label: t('inbox:dismiss'),
                icon: <ClearIcon fontSize="small" />,
                onClick: () => void clear.submit(notification),
              },
            ]}
          />
        )}
      </AsyncContent>
    </>
  );
}
